/**
 * THE ENTRY POINT, and it is deliberately almost empty.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHAT THIS IS FOR
 * ─────────────────────────────────────────────────────────────────────────
 *
 * `index-*.js` is about 1 MB, and 69% of it by source bytes is THREE. All of
 * it used to be a single static import graph hanging off `main.ts`, which
 * means the browser had to fetch, parse and RUN every byte of it before one
 * pixel of the app existed. Measured at 20x CPU throttle, headed Chromium,
 * against the production bundle:
 *
 *     first app markup   12,210 ms
 *     v8.evaluateModule   4,719 ms   (one call, the whole graph)
 *
 * Twelve seconds of the launch screen with nothing to show for it. On a real
 * phone at a real throttle it is smaller, but it is the same shape, and it is
 * the entire "why is it spinning" complaint.
 *
 * So the entry is now a file with NO static imports except the stylesheet. It
 * paints a front door immediately, then pulls the real app in behind it. The
 * total work is unchanged — this does not make THREE cheaper — but it stops
 * that work standing between the player and the screen.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE THREE RULES THAT MAKE IT WORK
 * ─────────────────────────────────────────────────────────────────────────
 *
 * 1. NO STATIC IMPORTS OF APP CODE. Anything imported here lands in the entry
 *    chunk and is evaluated before first paint, which is the thing being
 *    fixed. That is why the menu labels below are literals rather than an
 *    import of `TITLE_MENU`: importing it would drag `frontDoor.ts` in today,
 *    and whatever `frontDoor.ts` grows tomorrow. `test/boot.test.ts` asserts
 *    the two lists agree, so the duplication cannot drift silently.
 *
 * 2. THE STYLESHEET IS IMPORTED HERE, not in main.ts. Vite extracts the
 *    entry's CSS into a <link> that the browser fetches in parallel with the
 *    JS. Left on main.ts it would have arrived with the async chunk, and the
 *    skeleton would have spent those twelve seconds unstyled, which is worse
 *    than a blank screen rather than better.
 *
 * 3. THE TAP IS NOT LOST. A front door whose PLAY does nothing for several
 *    seconds is a worse lie than no front door at all. So a tap here is
 *    recorded and replayed against the real button once the app is live.
 */
import "./style.css";

/**
 * The menu, duplicated from TITLE_MENU on purpose. See rule 1.
 *
 * Labels and hints only. The skeleton has no icons: the plaque SVGs live in
 * frontDoor.ts and are not worth an import, and a plaque without its icon
 * reads as a plaque that has not finished loading, which is exactly what it
 * is.
 */
const SKELETON_MENU: ReadonlyArray<{ id: string; label: string }> = [
  { id: "play", label: "PLAY" },
  { id: "multiplayer", label: "MULTIPLAYER" },
  { id: "club", label: "CLUB" },
  { id: "brands", label: "BRANDS" },
  { id: "howto", label: "HOW TO PLAY" },
  { id: "shop", label: "SHOP" },
  { id: "settings", label: "SETTINGS" }
];

/** Exported for the drift test, which compares it against TITLE_MENU. */
export const SKELETON_MENU_IDS: readonly string[] = SKELETON_MENU.map((item) => item.id);
export const SKELETON_MENU_LABELS: readonly string[] = SKELETON_MENU.map((item) => item.label);

/**
 * The skeleton is INLINE IN index.html, not rendered here.
 *
 * It was rendered here first, and measured at 20x throttle it did not reach
 * the screen until 7,748ms: a deferred module script waits for pending
 * stylesheets, and index.css is ~100 KB. Static markup with its own inline
 * critical CSS waits for neither. So this file no longer paints anything; it
 * owns the hand-off, which is the part that genuinely needs JavaScript.
 */
const SKELETON_ID = "boot-skeleton";

/** What the player pressed before the app existed, if anything. */
let queuedTarget: string | null = null;

function boot(): void {
  const skeleton = document.getElementById(SKELETON_ID);

  // Capture, do not act. Bound to the skeleton itself, so it dies with it and
  // there is never a window where this and the app's own delegated listener
  // are both live and a tap counts twice.
  const capture = (event: Event): void => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-boot-target]");
    if (!target) return;
    event.preventDefault();
    queuedTarget = target.dataset.bootTarget ?? null;
    // A button that swallows a tap silently reads as broken.
    target.classList.add("is-busy");
  };
  skeleton?.addEventListener("click", capture);

  // AFTER THE SKELETON HAS PAINTED, not before. The import fetches the app's
  // chunks with preloads (see modulePreload in vite.config.ts), and WebKit
  // holds its first paint while preloads issued before it are in flight. Two
  // frames is "a frame has been presented"; the timer covers a page where
  // frames are not being produced (a background tab), so the app still loads.
  void afterFirstPaint()
    .then(() => import("./main"))
    .then(() => {
      skeleton?.removeEventListener("click", capture);
      // The app has painted its own front door; the stand-in has no more job.
      skeleton?.remove();
      replayQueuedTap();
    })
    .catch((error: unknown) => {
      skeleton?.removeEventListener("click", capture);
      // A failed entry import is a blank app forever, which is the one failure
      // mode worth shouting about from a file this small.
      console.error("[boot] the app failed to load", error);
      const note = document.createElement("p");
      note.textContent = "Something went wrong loading the game. Please reload.";
      skeleton?.appendChild(note);
    });
}

/**
 * Resolves once a frame has been presented, or after a short fallback.
 *
 * Costs at most a frame or two of delay on the import, which the parallel
 * chunk fetch it unlocks repays many times over on a real network.
 */
function afterFirstPaint(): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const go = (): void => {
      if (done) return;
      done = true;
      resolve();
    };
    requestAnimationFrame(() => requestAnimationFrame(go));
    setTimeout(go, 100);
  });
}

/**
 * Press the real button, once it is real.
 *
 * Replayed as a synthetic click on the app's own control rather than by
 * calling into it, so the tap takes exactly the path a finger would: the same
 * delegated listener, the same guards, the same busy state. Nothing here needs
 * to know what PLAY does.
 *
 * Retried across frames because main.ts renders on import but its first paint
 * is scheduled rather than synchronous. Bounded, because a resumed room paints
 * the board instead of the front door, and in that case there is no PLAY
 * button and pressing one would be wrong anyway.
 */
function replayQueuedTap(): void {
  const target = queuedTarget;
  queuedTarget = null;
  if (!target) return;

  let attempts = 0;
  const tryPress = (): void => {
    const button = document.querySelector<HTMLElement>(
      `[data-action="front-door"][data-target="${target}"]`
    );
    if (button) {
      button.click();
      return;
    }
    if (attempts < 60) {
      attempts += 1;
      requestAnimationFrame(tryPress);
    }
  };
  requestAnimationFrame(tryPress);
}

// Guarded so this module can be IMPORTED without booting. The drift test in
// test/boot.test.ts reads the menu constants above and runs in the node
// environment, where there is no document and calling boot() would throw
// before a single assertion ran.
if (typeof document !== "undefined") {
  boot();
}
